import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { ArrowLeft, Truck, Wallet, Zap, Leaf, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
  { icon: <Wallet className="w-4 h-4" />, text: "Behold 80% af hvert job, pengene er garanteret, før du kører ud" },
  { icon: <Zap className="w-4 h-4" />, text: "Automatisk udbetaling direkte til din bankkonto via Stripe" },
  { icon: <Leaf className="w-4 h-4" />, text: "Kører du ladcykel? Kunder kan vælge grøn transport" },
  { icon: <CheckCircle2 className="w-4 h-4" />, text: "Du bestemmer selv hvornår, hvor og hvor meget" },
];

export default function BecomeDriver() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [vehicle, setVehicle] = useState("");
  const [desc, setDesc] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!vehicle) { toast.error("Vælg dit transportmiddel"); return; }
    setLoading(true);
    try {
      await api.post("/profile/become-driver", { vehicle_type: vehicle, vehicle_description: desc });
      await refresh();
      toast.success("Velkommen som grabber! 🎉 Du er nu i chauffør-tilstand");
      navigate("/earnings");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Noget gik galt");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Tilbage
      </Link>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mb-4 shadow-lg shadow-primary/30">
          <Truck className="w-7 h-7" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Bliv grabber 🚀</h1>
        <p className="text-muted-foreground mb-8">Tjen penge på at hente og levere i dit område, med bil eller ladcykel.</p>

        <Card className="mb-6">
          <CardContent className="pt-5 space-y-3">
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">{b.icon}</div>
                <p>{b.text}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2">
            <Label>Dit transportmiddel *</Label>
            <Select value={vehicle} onValueChange={setVehicle}>
              <SelectTrigger data-testid="become-driver-vehicle"><SelectValue placeholder="Vælg transportmiddel" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Ladcykel">🚲 Ladcykel (grøn transport)</SelectItem>
                <SelectItem value="Cykel med trailer">🚲 Cykel med trailer (grøn transport)</SelectItem>
                <SelectItem value="Personbil">🚗 Personbil</SelectItem>
                <SelectItem value="Stationcar">🚙 Stationcar</SelectItem>
                <SelectItem value="Pickup">🛻 Pickup</SelectItem>
                <SelectItem value="Kassevogn">🚚 Kassevogn</SelectItem>
                <SelectItem value="Varevogn med lift">🚚 Varevogn med lift</SelectItem>
                <SelectItem value="Trailer">🚗 Bil med trailer</SelectItem>
                <SelectItem value="Lastbil">🚛 Lastbil</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Kort om dig og dit køretøj (valgfrit)</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Fx: Rummelig kassevogn med lift, kan tage store møbler..." rows={3} data-testid="become-driver-desc" />
          </div>
          <Button type="submit" size="lg" className="w-full rounded-xl font-semibold gap-2" disabled={loading} data-testid="become-driver-submit">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Start som grabber
          </Button>
          <p className="text-xs text-muted-foreground text-center">Bagefter hjælper vi dig med at tilkoble din udbetalingskonto, så du får pengene automatisk.</p>
        </form>
      </motion.div>
    </div>
  );
}
